<div
  class="ui-bar-chart"
  role="figure"
  [attr.aria-label]="title() || 'Gráfico de barras'"
>
  @if (title()) {
    <h3 class="ui-bar-chart__title">{{ title() }}</h3>
  }

  <div
    class="ui-bar-chart__canvas-wrap"
    (mousemove)="onMouseMove($event)"
    (mouseleave)="onCanvasLeave()"
  >
    @if (loading()) {
      <div
        class="ui-bar-chart__skeleton"
        [style.width.px]="chartWidth()"
        [style.height.px]="chartHeight()"
        aria-hidden="true"
      ></div>
    } @else {
      <svg
        class="ui-bar-chart__svg"
        [attr.width]="fillContainer() ? null : chartWidth()"
        [attr.height]="fillContainer() ? null : chartHeight()"
        [attr.viewBox]="'0 0 ' + chartWidth() + ' ' + chartHeight()"
        [attr.preserveAspectRatio]="fillContainer() ? 'none' : null"
        aria-hidden="true"
        overflow="visible"
      >
        <!-- Grupo principal desplazado por los márgenes -->
        <g [attr.transform]="'translate(' + chartMargin().left + ',' + chartMargin().top + ')'">

          <!-- ── Gridlines y eje Y (variante vertical) ───────────────── -->
          @if (variant() !== 'horizontal') {
            @for (tick of yTicks(); track tick.value) {
              @if (showGrid()) {
                <line
                  class="ui-bar-chart__grid"
                  [attr.x1]="0"
                  [attr.y1]="tick.y"
                  [attr.x2]="plotW()"
                  [attr.y2]="tick.y"
                />
              }
              <text
                class="ui-bar-chart__axis-label ui-bar-chart__axis-label--y"
                [attr.x]="-8"
                [attr.y]="tick.y"
                text-anchor="end"
                dominant-baseline="middle"
              >{{ tick.label }}</text>
            }

            <!-- Eje Y (línea) -->
            <line
              class="ui-bar-chart__axis"
              x1="0" [attr.y1]="0"
              x2="0" [attr.y2]="plotH()"
            />

            <!-- Línea base (eje X) -->
            <line
              class="ui-bar-chart__axis"
              [attr.x1]="0" [attr.y1]="plotH()"
              [attr.x2]="plotW()" [attr.y2]="plotH()"
            />

            <!-- Línea del cero (variante negative) -->
            @if (variant() === 'negative' && zeroY() < plotH()) {
              <line
                class="ui-bar-chart__zero-line"
                [attr.x1]="0" [attr.y1]="zeroY()"
                [attr.x2]="plotW()" [attr.y2]="zeroY()"
              />
            }

            <!-- Ticks eje X -->
            @for (tick of xTicks(); track tick.label) {
              <text
                class="ui-bar-chart__axis-label ui-bar-chart__axis-label--x"
                [attr.x]="tick.x"
                [attr.y]="plotH() + 16"
                text-anchor="middle"
                dominant-baseline="hanging"
              >{{ tick.label }}</text>
            }

            <!-- Etiqueta eje Y -->
            @if (yLabel()) {
              <text
                class="ui-bar-chart__axis-title"
                [attr.transform]="'rotate(-90) translate(' + (-(plotH()/2)) + ',' + (-44) + ')'"
                text-anchor="middle"
              >{{ yLabel() }}</text>
            }

            <!-- Etiqueta eje X -->
            @if (xLabel()) {
              <text
                class="ui-bar-chart__axis-title"
                [attr.x]="plotW() / 2"
                [attr.y]="plotH() + 38"
                text-anchor="middle"
              >{{ xLabel() }}</text>
            }
          }

          <!-- ── Ejes variante horizontal ─────────────────────────────── -->
          @if (variant() === 'horizontal') {
            @for (cat of categories(); track cat.id; let ci = $index) {
              <text
                class="ui-bar-chart__axis-label ui-bar-chart__axis-label--y"
                [attr.x]="-8"
                [attr.y]="(ci + 0.5) * (plotH() / categories().length)"
                text-anchor="end"
                dominant-baseline="middle"
              >{{ cat.label }}</text>
            }
            <!-- Línea base izquierda -->
            <line class="ui-bar-chart__axis" x1="0" y1="0" x2="0" [attr.y2]="plotH()" />
            <!-- Línea base inferior -->
            <line class="ui-bar-chart__axis" x1="0" [attr.y1]="plotH()" [attr.x2]="plotW()" [attr.y2]="plotH()" />
            <!-- Gridlines verticales para horizontal -->
            @if (showGrid()) {
              @for (tick of [0.25, 0.5, 0.75, 1]; track tick) {
                <line
                  class="ui-bar-chart__grid"
                  [attr.x1]="tick * plotW()"
                  y1="0"
                  [attr.x2]="tick * plotW()"
                  [attr.y2]="plotH()"
                />
              }
            }
          }

          <!-- ── Barras ───────────────────────────────────────────────── -->
          @if (variant() === 'stacked') {
            @for (group of stackedGroups(); track group.catIdx) {
              <g
                class="ui-bar-chart__bar-shell ui-bar-chart__bar-shell--stack"
                [class.ui-bar-chart__bar-shell--animated]="animated()"
                [style.--bar-delay]="group.animDelay"
              >
                @for (bar of group.bars; track bar.seriesIdx + '-' + bar.catIdx) {
                  <ng-container *ngTemplateOutlet="barMark; context: { $implicit: bar }" />
                }
              </g>
            }
          } @else {
            @for (bar of bars(); track bar.seriesIdx + '-' + bar.catIdx) {
              <g
                class="ui-bar-chart__bar-shell"
                [class.ui-bar-chart__bar-shell--animated]="animated()"
                [class.ui-bar-chart__bar-shell--horizontal]="variant() === 'horizontal'"
                [style.--bar-delay]="bar.animDelay"
                [style.--bar-h]="bar.h"
                [style.--bar-w]="bar.w"
                [style.--bar-x]="bar.x"
                [style.--bar-y]="bar.y"
                [style.--zero-y]="zeroY()"
                [style.--plot-w]="plotW()"
              >
                <ng-container *ngTemplateOutlet="barMark; context: { $implicit: bar }" />
              </g>
            }
          }
        </g>

        <ng-template #barMark let-bar>
              <!-- Visual: path con redondeado selectivo (solo la punta), sin pointer events -->
              <path
                class="ui-bar-chart__bar"
                [class.ui-bar-chart__bar--hovered]="hoveredKey() === (bar.seriesIdx + '-' + bar.catIdx)"
                [class.ui-bar-chart__bar--active]="isActive(bar)"
                [attr.d]="barPath(bar)"
                [attr.fill]="bar.color"
                [style.opacity]="barOpacity(bar)"
                [style.stroke]="isActive(bar) ? '#fff' : 'none'"
                [style.stroke-width]="isActive(bar) ? '1.5' : '0'"
                style="pointer-events: none;"
              />

              <!-- Etiqueta de valor -->
              @if (showLabel(bar)) {
                <text
                  class="ui-bar-chart__value-label"
                  [class.ui-bar-chart__value-label--inside]="labelMode() === 'inside'"
                  [attr.x]="bar.labelX"
                  [attr.y]="bar.labelY"
                  [attr.text-anchor]="bar.labelAnchor"
                  dominant-baseline="middle"
                  style="pointer-events: none;"
                >{{ formatValue(bar.value) }}</text>
              }

              <!-- Hit area: path idéntico al visual, transparente, captura eventos -->
              <path
                class="ui-bar-chart__hit-area"
                [attr.d]="barPath(bar)"
                fill="transparent"
                stroke="none"
                role="button"
                tabindex="0"
                [attr.aria-label]="
                  series()[bar.seriesIdx]?.label +
                  ': ' +
                  formatValue(bar.value)
                "
                (mouseenter)="onBarEnter(bar)"
                (mouseleave)="onBarLeave()"
                (click)="onBarClick(bar)"
                (keydown.enter)="onBarClick(bar)"
                (keydown.space)="$event.preventDefault(); onBarClick(bar)"
              />
        </ng-template>
      </svg>
    }

    <!-- Tooltip flotante -->
    @if (tooltipVisible() && tooltipBar()) {
      <div
        class="ui-bar-chart__tooltip"
        [class.ui-bar-chart__tooltip--ready]="tooltipReady()"
        [style.left.px]="tooltipX()"
        [style.top.px]="tooltipY()"
        aria-hidden="true"
      >
        <span
          class="ui-bar-chart__tooltip-dot"
          [style.background]="tooltipBar()!.color"
        ></span>
          <span
            class="ui-bar-chart__tooltip-body"
            [class.ui-bar-chart__tooltip-body--custom]="tooltipLabelFormatter()"
          >
            <span
              class="ui-bar-chart__tooltip-series"
              [class.ui-bar-chart__tooltip-series--custom]="tooltipLabelFormatter()"
            >
              {{ formatTooltipLabel(tooltipBar()!) }}
          </span>
          <span class="ui-bar-chart__tooltip-row">
              @if (!tooltipLabelFormatter()) {
                <span class="ui-bar-chart__tooltip-cat">
                  {{ categories()[tooltipBar()!.catIdx]?.label }}
                </span>
              }
            <span class="ui-bar-chart__tooltip-value">
              {{ formatTooltipValue(tooltipBar()!) }}
            </span>
          </span>
        </span>
      </div>
    }
  </div>

  <!-- Leyenda de series -->
  @if (showLegend() && !loading() && series().length > 1) {
    <ul class="ui-bar-chart__legend" role="list" aria-label="Leyenda del gráfico">
      @for (sr of series(); track sr.id; let i = $index) {
        <li class="ui-bar-chart__legend-item" role="listitem">
          <span
            class="ui-bar-chart__legend-dot"
            [style.background]="seriesColor(i)"
            aria-hidden="true"
          ></span>
          <span class="ui-bar-chart__legend-label">{{ sr.label }}</span>
        </li>
      }
    </ul>
  }
</div>
